import { FormError } from '@kcftech/design-system';
import { Asterisk } from 'components/forms/Label';
import { NumericOptionDropdown } from 'components/dropdown/NumericOptionDropdown';
import { ContainedTextInput } from 'components/node_settings/V4EditSettingsModal/styled';
import { V4SensorSettings } from 'custom_hooks/data_hooks/v4_sensor_settings/shared';
import { useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { RequiredMessage } from 'shared/form/RequiredMessage';
import styled from 'styled-components';
import { SmallSecondaryText } from 'styles/theme_styles/text';
import { tokens } from 'styles/tokens';
import { convertOffsetUnit, OffsetUnit } from 'utils/duration';
import { OptionChild } from 'utils/node';

const TriggeredFields = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
`;

const TriggeredSubBlock = styled.div<{ $divider?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    padding-bottom: 0.6rem;
    border-bottom: ${({ $divider = true }) =>
        $divider ? `1px solid ${tokens.border.default}` : 'none'};
`;

const TriggeredFieldRow = styled.div`
    display: flex;
    align-items: center;
    gap: 1.6rem;
`;

const TriggeredFieldLabel = styled.div`
    flex: 0 0 auto;
    min-width: 18rem;
    font-size: 1.4rem;
    font-weight: 500;
    line-height: 1.8rem;
`;

const TriggeredFieldValue = styled.div`
    display: flex;
    align-items: center;
    gap: 0.8rem;
`;

const FieldColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
`;

const UnitButton = styled.button<{ $selected: boolean }>`
    height: 2.4rem;
    padding: 0 1.2rem;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-weight: 500;
    font-size: 1.4rem;
    line-height: 1.8rem;
    background: ${({ $selected }) =>
        $selected ? tokens.button.primaryBackground : tokens.button.secondaryBackground};
    color: ${({ $selected }) =>
        $selected ? tokens.button.primaryText : tokens.button.secondaryText};
`;

type TriggeredSettingsProps = Readonly<{
    // TODO: replace with a V4-specific list once hardware finalizes the spec.
    updateIntervalOptions: OptionChild<number>[];
}>;

export function TriggeredSettings({ updateIntervalOptions }: TriggeredSettingsProps) {
    const { control } = useFormContext<V4SensorSettings>();
    const updateInterval = useWatch({ control, name: 'updateInterval' });
    // ms/s is UI-only; the form always stores ms.
    const [unit, setUnit] = useState<OffsetUnit>('ms');
    // String buffers preserve intermediate keystrokes ("2.") that controlled inputs would clobber.
    // Guard against NaN (a blanked field) so a tab switch re-mounts to an empty input, not "NaN".
    const [thresholdInput, setThresholdInput] = useState(() =>
        updateInterval.mode === 'triggered' && Number.isFinite(updateInterval.thresholdG)
            ? String(updateInterval.thresholdG)
            : '',
    );
    const [offsetInput, setOffsetInput] = useState(() =>
        updateInterval.mode === 'triggered' &&
        Number.isFinite(updateInterval.collectionOffsetMs)
            ? String(updateInterval.collectionOffsetMs)
            : '',
    );

    if (updateInterval.mode !== 'triggered') {
        return null;
    }

    const handleUnitChange = (next: OffsetUnit) => {
        if (next === unit) {
            return;
        }
        setOffsetInput((prev) => convertOffsetUnit(prev, unit, next));
        setUnit(next);
    };

    // Show the saved ms value; fall back to the raw entry while it's invalid (the error flags it).
    const offsetHelperDisplay = Number.isFinite(updateInterval.collectionOffsetMs)
        ? unit === 's'
            ? convertOffsetUnit(String(updateInterval.collectionOffsetMs), 'ms', 's')
            : String(updateInterval.collectionOffsetMs)
        : offsetInput;

    return (
        <TriggeredFields>
            <TriggeredSubBlock>
                <RequiredMessage />
                <TriggeredFieldRow>
                    <TriggeredFieldLabel>
                        Threshold (0.1-25 g)&nbsp;
                        <Asterisk aria-hidden="true">*</Asterisk>
                    </TriggeredFieldLabel>
                    <TriggeredFieldValue>
                        <Controller
                            control={control}
                            name="updateInterval.thresholdG"
                            rules={{
                                validate: (value) =>
                                    (Number.isFinite(value) &&
                                        value >= 0.1 &&
                                        value <= 25) ||
                                    'Enter a threshold between 0.1 and 25 g',
                            }}
                            render={({ field, fieldState }) => (
                                <FieldColumn>
                                    <ContainedTextInput
                                        inputMode="decimal"
                                        placeholder="Enter value"
                                        aria-label="Threshold (0.1-25 g)"
                                        aria-required="true"
                                        aria-invalid={fieldState.error !== undefined}
                                        value={thresholdInput}
                                        onChange={(e) => {
                                            setThresholdInput(e.target.value);
                                            const parsed = Number(e.target.value);
                                            // NaN, not 0, so validation flags unparseable input instead of silently accepting it.
                                            field.onChange(
                                                e.target.value.trim() !== '' &&
                                                    Number.isFinite(parsed)
                                                    ? parsed
                                                    : Number.NaN,
                                            );
                                        }}
                                    />
                                    <FormError message={fieldState.error?.message} />
                                </FieldColumn>
                            )}
                        />
                    </TriggeredFieldValue>
                </TriggeredFieldRow>
                {/* TODO: source the past-week trend min/max from the sensor's data instead of hardcoding. */}
                <SmallSecondaryText>
                    Past week trend: min 1.1246 g, max 16.0785 g
                </SmallSecondaryText>
            </TriggeredSubBlock>

            <TriggeredSubBlock>
                <TriggeredFieldRow>
                    <TriggeredFieldLabel>Collection Offset</TriggeredFieldLabel>
                    <Controller
                        control={control}
                        name="updateInterval.collectionOffsetMs"
                        rules={{
                            validate: (value) =>
                                (Number.isFinite(value) && value >= 0) ||
                                'Enter a collection offset of 0 ms or more',
                        }}
                        render={({ field, fieldState }) => (
                            <FieldColumn>
                                <TriggeredFieldValue>
                                    <ContainedTextInput
                                        inputMode={unit === 'ms' ? 'numeric' : 'decimal'}
                                        placeholder="Enter value"
                                        aria-label="Collection Offset"
                                        aria-invalid={fieldState.error !== undefined}
                                        value={offsetInput}
                                        onChange={(e) => {
                                            setOffsetInput(e.target.value);
                                            const ms = Number(
                                                convertOffsetUnit(
                                                    e.target.value,
                                                    unit,
                                                    'ms',
                                                ),
                                            );
                                            // NaN, not 0, so invalid input blocks save instead of silently saving 0.
                                            field.onChange(
                                                e.target.value.trim() !== '' &&
                                                    Number.isFinite(ms)
                                                    ? ms
                                                    : Number.NaN,
                                            );
                                        }}
                                    />
                                    <UnitButton
                                        type="button"
                                        $selected={unit === 'ms'}
                                        aria-pressed={unit === 'ms'}
                                        onClick={() => handleUnitChange('ms')}
                                    >
                                        ms
                                    </UnitButton>
                                    <UnitButton
                                        type="button"
                                        $selected={unit === 's'}
                                        aria-pressed={unit === 's'}
                                        onClick={() => handleUnitChange('s')}
                                    >
                                        s
                                    </UnitButton>
                                </TriggeredFieldValue>
                                <FormError message={fieldState.error?.message} />
                            </FieldColumn>
                        )}
                    />
                </TriggeredFieldRow>
                <SmallSecondaryText>
                    Data capture is delayed until{' '}
                    <strong>
                        {offsetHelperDisplay} {unit}
                    </strong>{' '}
                    after the trigger point.
                </SmallSecondaryText>
            </TriggeredSubBlock>

            <TriggeredSubBlock $divider={false}>
                <TriggeredFieldRow>
                    <TriggeredFieldLabel>
                        Minimum Time Between Triggers
                    </TriggeredFieldLabel>
                    <Controller
                        control={control}
                        name="updateInterval.minTimeBetweenTriggersMs"
                        render={({ field }) => (
                            <NumericOptionDropdown
                                value={field.value}
                                options={updateIntervalOptions}
                                onChange={field.onChange}
                            />
                        )}
                    />
                </TriggeredFieldRow>
            </TriggeredSubBlock>
        </TriggeredFields>
    );
}
